import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import { addCurrentThemeClassToEle } from "@/utils";
import { type NoticeItem, isStudentNoticeUnread } from "@/pages/notification/data";
import { useMediaQuery } from "@/pages/notification/useMediaQuery";
import { NotificationList } from "./components/NotificationList";
import { NotificationDetail } from "./components/NotificationDetail";
import { getDashboardDetailsApiCall, updateStudentNoticeReadStatusApiCall } from "@/services/api";
import {
  markStudentNoticeRead,
  setDashboardDetails,
} from "@/store/slices/dashboard/dashboardDetailsSlice";
import {
  handleUpdateUserPurchaseContentApiCall,
  handleUpdateWelcomeSeriesContentApiCall,
} from "@/utils/apiUtils";
import "./style.css";
import { BookOpenIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useSafeAreaInsets } from "@/hooks/useSafeAreaInsets";

function countUnread(items: NoticeItem[]) {
  return items.reduce((acc, n) => acc + (n.isUnread ? 1 : 0), 0);
}

export default function NotificationRoute() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const currentTheme = useSelector((state: RootState) => state.currentThemeReducer.currentTheme);
  const dashboardDetails = useSelector((state: RootState) => state.dashboardDetailsReducer.dashboardDetails);
  const { topSafeComponent } = useSafeAreaInsets();
  const [items, setItems] = useState<NoticeItem[]>([]);
  const [desktopListOpen, setDesktopListOpen] = useState(true);
  const unreadCount = useMemo(() => countUnread(items), [items]);

  const selected = useMemo(
    () => (id ? items.find((n) => n.id === id) : undefined),
    [id, items]
  );

  useEffect(() => {
    const hasApiNotices =
      Array.isArray(dashboardDetails?.studentNotices) && dashboardDetails.studentNotices.length > 0;

    if (hasApiNotices) return;

    const fetchDashboard = async () => {
      const payload = { purchaseId: null };
      const ok1 = await handleUpdateUserPurchaseContentApiCall(payload);
      const ok2 = await handleUpdateWelcomeSeriesContentApiCall(payload);
      if (!ok1 || !ok2) return;

      const res = await getDashboardDetailsApiCall();
      if (res?.status && res?.data) {
        dispatch(setDashboardDetails(res.data));
      }
    };

    fetchDashboard();
  }, [dispatch, dashboardDetails]);

  useEffect(() => {
    const apiNotices = (dashboardDetails?.studentNotices ?? []).map((n: any) => ({
      id: n._id,
      title: n.title,
      description: n.contentDescription,
      createdAt: n.createdAt,
      isUnread: isStudentNoticeUnread(n),
    })) as NoticeItem[];

    setItems(apiNotices);
  }, [dashboardDetails]);

  useEffect(() => {
    if (!id) return;

    const raw = (dashboardDetails?.studentNotices ?? []).find((n: any) => n?._id === id);
    if (!raw || !isStudentNoticeUnread(raw)) {
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isUnread: false } : n)));
      return;
    }

    let cancelled = false;
    (async () => {
      const res = await updateStudentNoticeReadStatusApiCall(id);
      if (cancelled) return;
      if (res?.status) {
        dispatch(markStudentNoticeRead({ noticeId: id }));
        setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isUnread: false } : n)));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, dashboardDetails?.studentNotices, dispatch]);

  const onBack = () => {
    if (isDesktop) {
      navigate("/", { replace: true });
      return;
    }
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  const openNotification = (nextId: string) => {
    navigate(`/notification/${encodeURIComponent(nextId)}`);
  };

  if (!isDesktop) {
    return (
      <div className={`ntf-page mt-[45px] min-[1280px]:mt-0 ${addCurrentThemeClassToEle(currentTheme, "ntf-page")}`}>
        {topSafeComponent}
        <header
          className={`ntf-header miniNav ${addCurrentThemeClassToEle(currentTheme, 'miniNav')}  
          border-0 
          mb-[-15px]
          ${id ? "ntf-header--detail" : "ntf-header--list"} 
          ${addCurrentThemeClassToEle(
            currentTheme,
            "ntf-header"
          )}
          `}
        >
          <button type="button" className="ntf-back" onClick={onBack} aria-label="Back">
            <ChevronLeftIcon className="ntf-backIcon ms-[-30px]" size={27} strokeWidth={2} aria-hidden />
          </button>
          {!id ? (
            <>
              <div className="ntf-header-center">
                <span className="ntf-header-title">Notices</span>
                <span className="ntf-header-badge" aria-label={`${unreadCount} unread`}>
                  {unreadCount}
                </span>
              </div>
              <div className="ntf-header-trailingSpacer" aria-hidden="true" />
            </>
          ) : null}
        </header>

        <main className="ntf-content">
          {id ? (
            <NotificationDetail notification={selected} />
          ) : (
            <NotificationList items={items} selectedId={undefined} onOpen={openNotification} />
          )}
        </main>
      </div>
    );
  }

  return (
    <div className={`ntf-desktop mt-[45px] min-[1280px]:mt-0 ${addCurrentThemeClassToEle(currentTheme, "ntf-desktop")}`}>
      <div className={`ntf-left ${desktopListOpen ? "" : "is-list-hidden"}`}>
        <div className="ntf-leftHeader">
          <ChevronLeftIcon className="ntf-icon" size={16} onClick={onBack} aria-label="Back"/>
          <div className="ntf-leftHeaderTitle">{desktopListOpen ? "All Notices" : "Back"}</div>
          <button
            type="button"
            className="ntf-collapseBtn"
            onClick={() => setDesktopListOpen((v) => !v)}
            aria-label={desktopListOpen ? "Hide notices list" : "Show notices list"}
            title={desktopListOpen ? "Hide list" : "Show list"}
          >
            {desktopListOpen ?
             <ChevronLeftIcon className="ntf-icon" size={13}/> : 
             <ChevronRightIcon className="ntf-icon" size={13}/>}
          </button>
        </div>

        <div className="ntf-leftBody">
          <aside className="ntf-sidebar">
            <div className="ntf-sidebar-card">
              <BookOpenIcon className="ntf-icon text-[#009D3F]" size={20}/>
              <div className="ntf-sidebar-title">Notices</div>
            </div>
          </aside>

          <section className={`ntf-listPanel ${desktopListOpen ? "" : "is-collapsed"}`}>
            <div className="ntf-listScroll">
              <NotificationList
                items={items}
                selectedId={id}
                onOpen={openNotification}
                showChevron={false}
              />
            </div>
          </section>
        </div>
      </div>

      <section className="ntf-detailPanel">
        <NotificationDetail notification={selected} />
      </section>
    </div>
  );
}

